import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { submitPartnerApplication, REQUIRED_DOC_KEYS, OPTIONAL_DOC_KEYS } from '@/lib/partnerApplications';
import type { PartnerDocFiles, PartnerDocKey } from '@/lib/partnerApplications';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import PartnerApplyV2 from './v2/PartnerApplyV2';

const fileInputClass =
  'w-full px-4 py-2.5 rounded-xl bg-white border border-[#E8E8E6] text-xs text-[#2A2A2A] file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-[#F0EFED] file:text-[11px] file:font-medium file:text-[#2A2A2A] cursor-pointer';

export default function PartnerApplyPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <PartnerApplyV2 /> : <ClassicPartnerApplyPage />;
}

function ClassicPartnerApplyPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const docField = (key: PartnerDocKey, labelKey: string, numberField?: { name: string; labelKey: string }) => {
    const required = (REQUIRED_DOC_KEYS as readonly string[]).includes(key);
    return (
      <div className="flex flex-col gap-3" key={key}>
        {numberField && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
              {t(numberField.labelKey)} {required && '*'}
            </label>
            <input
              name={numberField.name}
              required={required}
              type="text"
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">{t(labelKey)}</label>
          <input name={key} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" required={required} className={fileInputClass} />
        </div>
      </div>
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const formData = new FormData(e.currentTarget);
    const get = (key: string) => (formData.get(key) as string | null)?.trim() || null;

    const files: PartnerDocFiles = {};
    for (const key of [...REQUIRED_DOC_KEYS, ...OPTIONAL_DOC_KEYS]) {
      const file = formData.get(key) as File | null;
      if (file && file.size > 0) files[key] = file;
    }

    const { error } = await submitPartnerApplication(
      {
        business_name: get('businessName') ?? '',
        business_type: get('businessType'),
        contact_person: get('contactPerson') ?? '',
        email: get('email') ?? '',
        phone: get('phone') ?? '',
        city: get('city'),
        address: get('address'),
        map_link: get('mapLink'),
        monthly_volume: get('volume'),
        why_partner: get('whyPartner'),
        drug_license_no: get('drugLicenseNo') ?? '',
        tin_no: get('tinNo') ?? '',
        nid_no: get('nidNo') ?? '',
        bin_no: get('binNo'),
      },
      files,
    );

    if (error) {
      setSubmitting(false);
      setSubmitError(error);
      return;
    }
    navigate(prefix + '/partner-apply/success');
  };

  const benefits = [
    'partnerApply.benefits.brandSupport',
    'partnerApply.benefits.training',
    'partnerApply.benefits.exclusive',
    'partnerApply.benefits.margin',
  ];

  return (
    <>
      <Seo pageKey="partnerApply" />
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white font-sans pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Hero */}
          <div className="flex flex-col gap-4 mb-12 md:mb-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('partnerApply.hero.label')}
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A2A2A] leading-[1.1]">
              {t('partnerApply.hero.heading')}
            </h1>
            <p className="text-sm text-[#8A8A8A] font-light max-w-lg">
              {t('partnerApply.hero.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5 p-6 md:p-8 rounded-2xl bg-[#FAFAF9] border border-[#F0EFED]"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Business Name */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.businessName')} *
                    </label>
                    <input
                      name="businessName"
                      required
                      type="text"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    />
                  </div>

                  {/* Business Type */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.businessType')} *
                    </label>
                    <select
                      name="businessType"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    >
                      <option value="">{t('common.select')}</option>
                      <option value="pharmacy">{t('partnerApply.form.typePharmacy')}</option>
                      <option value="beauty">{t('partnerApply.form.typeBeauty')}</option>
                      <option value="online">{t('partnerApply.form.typeOnline')}</option>
                      <option value="distributor">{t('partnerApply.form.typeDistributor')}</option>
                    </select>
                  </div>

                  {/* Contact Person */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.contactPerson')} *
                    </label>
                    <input
                      name="contactPerson"
                      required
                      type="text"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.email')} *
                    </label>
                    <input
                      name="email"
                      required
                      type="email"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    />
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.phone')} *
                    </label>
                    <input
                      name="phone"
                      required
                      type="tel"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    />
                  </div>

                  {/* City */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                      {t('partnerApply.form.city')} *
                    </label>
                    <select
                      name="city"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                    >
                      <option value="">{t('common.select')}</option>
                      <option value="dhaka">{t('partnerApply.form.cityDhaka')}</option>
                      <option value="chittagong">{t('partnerApply.form.cityChittagong')}</option>
                      <option value="sylhet">{t('partnerApply.form.citySylhet')}</option>
                      <option value="others">{t('partnerApply.form.cityOthers')}</option>
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                    {t('partnerApply.form.address')} *
                  </label>
                  <input
                    name="address"
                    required
                    type="text"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                  />
                </div>

                {/* Google Maps Link */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                    {t('partnerApply.form.mapLink')}
                  </label>
                  <input
                    name="mapLink"
                    type="url"
                    placeholder="https://maps.app.goo.gl/..."
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                  />
                  <p className="text-[11px] text-[#8A8A8A]">{t('partnerApply.form.mapLinkHint')}</p>
                </div>

                {/* Monthly Volume */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                    {t('partnerApply.form.volume')}
                  </label>
                  <select
                    name="volume"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:ring-1 focus:ring-[#7B1113]"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="<100">{t('partnerApply.form.volume1')}</option>
                    <option value="100-500">{t('partnerApply.form.volume2')}</option>
                    <option value="500-1000">{t('partnerApply.form.volume3')}</option>
                    <option value="1000+">{t('partnerApply.form.volume4')}</option>
                  </select>
                </div>

                {/* Why partner */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] tracking-wider uppercase text-[#8A8A8A] font-medium">
                    {t('partnerApply.form.whyPartner')}
                  </label>
                  <textarea
                    name="whyPartner"
                    rows={4}
                    maxLength={500}
                    placeholder={t('partnerApply.form.whyPlaceholder')}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:ring-1 focus:ring-[#7B1113] resize-none"
                  />
                  <span className="text-[10px] text-[#8A8A8A] font-light">500 {t('common.max')}</span>
                </div>

                {/* Required Documents */}
                <div className="flex flex-col gap-4 pt-2 border-t border-[#F0EFED]">
                  <h3 className="text-[11px] tracking-[0.2em] uppercase text-[#8A8A8A] font-medium pt-3">
                    {t('partnerApply.form.documentsTitle')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {docField('drug_license', 'partnerApply.form.docDrugLicense', { name: 'drugLicenseNo', labelKey: 'partnerApply.form.drugLicenseNo' })}
                    {docField('tin', 'partnerApply.form.docTin', { name: 'tinNo', labelKey: 'partnerApply.form.tinNo' })}
                    {docField('nid', 'partnerApply.form.docNid', { name: 'nidNo', labelKey: 'partnerApply.form.nidNo' })}
                    {docField('photo', 'partnerApply.form.docPhoto')}
                    {docField('bin', 'partnerApply.form.docBin', { name: 'binNo', labelKey: 'partnerApply.form.binNo' })}
                    {docField('bank_solvency', 'partnerApply.form.docBankSolvency')}
                    {docField('bank_statement', 'partnerApply.form.docBankStatement')}
                  </div>
                </div>

                {submitError && <p className="text-xs text-[#7B1113]">{submitError}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-2 w-full sm:w-auto px-8 py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center disabled:opacity-50"
                >
                  {submitting ? t('common.submitting') : t('partnerApply.form.submit')}
                </button>
              </form>
            </div>

            {/* Sidebar Benefits */}
            <div className="flex flex-col gap-6">
              <div className="p-6 rounded-2xl bg-[#F0EFED]">
                <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-4">
                  {t('partnerApply.benefits.title')}
                </h3>
                <div className="flex flex-col gap-3">
                  {benefits.map((key) => (
                    <div key={key} className="flex items-start gap-2">
                      <i className="ri-check-line text-[#2D6A4F] text-sm mt-0.5"></i>
                      <span className="text-xs text-[#2A2A2A] font-light">{t(key)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-[#1A1A1A] text-white">
                <h3 className="text-sm tracking-[0.12em] uppercase font-medium mb-2">
                  {t('contact.info.title')}
                </h3>
                <p className="text-xs text-white/60 font-light">
                  {t('faq.contactUs')}
                </p>
                <Link
                  to={prefix + '/contact'}
                  className="mt-3 inline-block text-xs text-[#7B1113] font-medium hover:underline"
                >
                  {t('common.contact')} →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SharedFooter />
    </>
  );
}